import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { getSupabaseClient } from '../../lib/supabase';
import { applyPinSession, fetchCurrentUser, submitPin } from './api';
import { AuthContext, type AuthContextValue } from './AuthContext';
import { InvalidUserSessionError } from './authErrors';
import type { AppUser } from './types';
import { useIdleLock } from './useIdleLock';

const PROFILE_TIMEOUT_MS = 10_000;
const PROFILE_ERROR =
  'Povezave ni bilo mogoče preveriti. Preverite povezavo in poskusite znova.';
const LOCKED_SESSION_STORAGE_KEY = 'wms-session-locked';

function readPersistedLock(): boolean {
  return window.sessionStorage.getItem(LOCKED_SESSION_STORAGE_KEY) === 'true';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(readPersistedLock);
  const userRef = useRef<AppUser | null>(null);
  const sessionUserIdRef = useRef<string | null>(null);
  const profileRequestRef = useRef<{
    userId: string;
    promise: Promise<boolean>;
  } | null>(null);
  const profileAbortRef = useRef<AbortController | null>(null);
  const deferredLoadRef = useRef<number | null>(null);

  const updateUser = useCallback((nextUser: AppUser | null) => {
    userRef.current = nextUser;
    setUser(nextUser);
  }, []);

  const updateLock = useCallback((nextLocked: boolean) => {
    if (nextLocked) {
      window.sessionStorage.setItem(LOCKED_SESSION_STORAGE_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(LOCKED_SESSION_STORAGE_KEY);
    }
    setLocked(nextLocked);
  }, []);

  const clearSessionState = useCallback(() => {
    if (deferredLoadRef.current !== null) {
      window.clearTimeout(deferredLoadRef.current);
      deferredLoadRef.current = null;
    }
    sessionUserIdRef.current = null;
    profileAbortRef.current?.abort();
    profileAbortRef.current = null;
    profileRequestRef.current = null;
    updateUser(null);
    setError(null);
    updateLock(false);
    setLoading(false);
  }, [updateLock, updateUser]);

  const loadUser = useCallback(
    (authUserId: string): Promise<boolean> => {
      sessionUserIdRef.current = authUserId;
      if (userRef.current?.authUserId === authUserId) {
        setError(null);
        setLoading(false);
        return Promise.resolve(true);
      }

      const pending = profileRequestRef.current;
      if (pending?.userId === authUserId) return pending.promise;

      profileAbortRef.current?.abort();
      const controller = new AbortController();
      profileAbortRef.current = controller;
      setLoading(true);
      setError(null);

      const timeoutId = window.setTimeout(
        () => controller.abort(),
        PROFILE_TIMEOUT_MS,
      );
      const promise = fetchCurrentUser(authUserId, controller.signal)
        .then((nextUser) => {
          if (sessionUserIdRef.current !== authUserId) return false;
          updateUser(nextUser);
          return true;
        })
        .catch(async (caught: unknown) => {
          if (sessionUserIdRef.current === authUserId) {
            if (caught instanceof InvalidUserSessionError) {
              try {
                await getSupabaseClient().auth.signOut({ scope: 'local' });
              } finally {
                if (sessionUserIdRef.current === authUserId) {
                  clearSessionState();
                }
              }
            } else {
              updateUser(null);
              setError(PROFILE_ERROR);
            }
          }
          return false;
        })
        .finally(() => {
          window.clearTimeout(timeoutId);
          if (profileAbortRef.current === controller) {
            profileAbortRef.current = null;
          }
          if (profileRequestRef.current?.promise === promise) {
            profileRequestRef.current = null;
          }
          if (sessionUserIdRef.current === authUserId) setLoading(false);
        });

      profileRequestRef.current = { userId: authUserId, promise };
      return promise;
    },
    [clearSessionState, updateUser],
  );

  const deferUserLoad = useCallback(
    (authUserId: string) => {
      if (deferredLoadRef.current !== null) {
        window.clearTimeout(deferredLoadRef.current);
      }
      deferredLoadRef.current = window.setTimeout(() => {
        deferredLoadRef.current = null;
        void loadUser(authUserId);
      }, 0);
    },
    [loadUser],
  );

  useEffect(() => {
    const supabase = getSupabaseClient();
    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        if (!session?.user.id) {
          clearSessionState();
          return;
        }

        sessionUserIdRef.current = session.user.id;
        if (
          event === 'TOKEN_REFRESHED' &&
          userRef.current?.authUserId === session.user.id
        ) {
          return;
        }
        deferUserLoad(session.user.id);
      },
    );

    return () => {
      subscription.subscription.unsubscribe();
      if (deferredLoadRef.current !== null) {
        window.clearTimeout(deferredLoadRef.current);
        deferredLoadRef.current = null;
      }
      sessionUserIdRef.current = null;
      profileAbortRef.current?.abort();
    };
  }, [clearSessionState, deferUserLoad]);

  const lock = useCallback(() => updateLock(true), [updateLock]);
  useIdleLock(Boolean(user) && !locked, lock, user?.sessionLockMinutes ?? 10);

  const authenticate = useCallback(
    async (pin: string) => {
      const response = await submitPin(pin);
      const newSessionUserId = await applyPinSession(response);
      const authUserId =
        newSessionUserId ??
        userRef.current?.authUserId ??
        sessionUserIdRef.current;
      if (!authUserId || !(await loadUser(authUserId))) {
        throw new Error('Prijava ni uspela.');
      }
      updateLock(false);
    },
    [loadUser, updateLock],
  );

  const retry = useCallback(() => {
    const authUserId = sessionUserIdRef.current;
    if (authUserId) void loadUser(authUserId);
  }, [loadUser]);

  const signOut = useCallback(async () => {
    await getSupabaseClient().auth.signOut();
    clearSessionState();
  }, [clearSessionState]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      locked,
      signInWithPin: authenticate,
      unlockWithPin: authenticate,
      retry,
      lock,
      signOut,
    }),
    [authenticate, error, loading, lock, locked, retry, signOut, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
